/**
 * GCLID Tracker
 *
 * Captures gclid and UTM parameters, stores them in cookies/localStorage,
 * and writes them to Shopify cart attributes so they persist through checkout.
 *
 * Cart attributes are server-side storage that survives the redirect to
 * checkout.shopify.com (which can't read client-side cookies).
 */
(function() {
  'use strict';

  const TRACKING_PARAMS = ['gclid', 'fbclid', 'ttclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const COOKIE_EXPIRY_DAYS = 90;
  const STORAGE_KEY = '_pf_tracking';

  /**
   * Get URL parameters
   */
  function getUrlParams() {
    const params = {};
    const searchParams = new URLSearchParams(window.location.search);
    TRACKING_PARAMS.forEach(param => {
      const value = searchParams.get(param);
      if (value) params[param] = value;
    });
    return params;
  }

  /**
   * Set a cookie
   */
  function setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  }

  /**
   * Get a cookie
   */
  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  /**
   * Store tracking data in cookie and localStorage
   */
  function storeTrackingData(params) {
    if (Object.keys(params).length === 0) return;

    // Merge with existing data (don't overwrite if no new value)
    const existing = getStoredTrackingData();
    const merged = { ...existing, ...params, _updated: Date.now() };

    const json = JSON.stringify(merged);

    // Store in localStorage
    try {
      localStorage.setItem(STORAGE_KEY, json);
    } catch (e) { /* localStorage might be blocked */ }

    // Store in cookie as backup
    setCookie(STORAGE_KEY, json, COOKIE_EXPIRY_DAYS);

    // Store gclid in _gcl_aw format for Google compatibility
    if (params.gclid) {
      const gclValue = `GCL.${Math.floor(Date.now() / 1000)}.${params.gclid}`;
      setCookie('_gcl_aw', gclValue, COOKIE_EXPIRY_DAYS);
    }
  }

  /**
   * Get stored tracking data
   */
  function getStoredTrackingData() {
    let data = null;

    // Try localStorage first
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) data = JSON.parse(stored);
    } catch (e) { /* ignore */ }

    // Fall back to cookie
    if (!data) {
      const cookieData = getCookie(STORAGE_KEY);
      if (cookieData) {
        try {
          data = JSON.parse(cookieData);
        } catch (e) { /* ignore */ }
      }
    }

    return data || {};
  }

  /**
   * Update cart attributes with tracking data
   */
  async function updateCartAttributes() {
    const tracking = getStoredTrackingData();
    if (Object.keys(tracking).length === 0) return;

    // Build attributes object for Shopify
    const attributes = {};
    TRACKING_PARAMS.forEach(param => {
      if (tracking[param]) {
        attributes[param] = tracking[param];
      }
    });

    if (Object.keys(attributes).length === 0) return;

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes })
      });
    } catch (e) {
      console.warn('[gclid-tracker] Failed to update cart attributes:', e);
    }
  }

  /**
   * Initialize on page load
   */
  function init() {
    // 1. Capture params from URL
    const urlParams = getUrlParams();

    // 2. Store if we have new tracking params
    if (Object.keys(urlParams).length > 0) {
      storeTrackingData(urlParams);
      // Update cart immediately if we have new tracking data
      updateCartAttributes();
    }

    // 3. Hook into add-to-cart to ensure attributes are set
    document.addEventListener('submit', function(e) {
      const form = e.target;
      if (form.action && form.action.includes('/cart/add')) {
        updateCartAttributes();
      }
    }, true);

    // 4. Hook into checkout buttons
    document.addEventListener('click', function(e) {
      const target = e.target.closest('a[href*="/checkout"], button[name="checkout"], input[name="checkout"]');
      if (target) {
        // Update cart attributes before checkout redirect
        updateCartAttributes();
      }
    }, true);
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
